<template>
  <Container>
    <template #header>
      <HeaderFe />
    </template>
    <template #hero>
      <PageHero page="profil" title="Profil Jurusan" current="Profil"
        subtitle="Visi, misi, dan akreditasi jurusan." />
    </template>
    <template v-slot:default>
      <main class="min-h-screen bg-gray-50">
        <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 py-12 space-y-10">

          <!-- Visi -->
          <article v-if="p.visi"
            class="relative p-6 overflow-hidden bg-white border border-gray-100 shadow-sm sm:p-8 rounded-2xl">
            <div class="absolute top-0 left-0 w-1.5 h-full bg-primary"></div>
            <div class="flex items-center gap-3 mb-5">
              <span class="flex items-center justify-center text-white w-11 h-11 rounded-xl bg-primary">
                <i class="fa-solid fa-bullseye"></i>
              </span>
              <h2 class="text-xl font-bold text-gray-900 sm:text-2xl">Visi</h2>
            </div>
            <div class="leading-relaxed prose text-gray-700 prose-gray max-w-none" v-html="p.visi"></div>
          </article>

          <!-- Akreditasi -->
          <article v-if="p.akreditasi"
            class="relative p-6 overflow-hidden text-white shadow-md sm:p-8 rounded-2xl bg-gradient-to-br from-primary to-primary/80">
            <i class="absolute text-white fa-solid fa-award -right-4 -bottom-4 text-9xl opacity-10"></i>
            <div class="relative flex items-center gap-3 mb-5">
              <span class="flex items-center justify-center w-11 h-11 rounded-xl bg-white/15 backdrop-blur">
                <i class="fa-solid fa-award"></i>
              </span>
              <h2 class="text-xl font-bold sm:text-2xl">Akreditasi</h2>
            </div>
            <div class="relative leading-relaxed prose prose-invert max-w-none" v-html="p.akreditasi"></div>
          </article>

          <!-- Pimpinan Jurusan -->
          <section v-if="formasi.data && formasi.data.length">
            <h2 class="mb-6 text-2xl font-bold text-gray-900">Pimpinan Jurusan</h2>
            <div
              class="overflow-hidden rounded-3xl bg-gradient-to-b from-white/70 to-white/100 shadow-lg">
              <div class="flex flex-col items-stretch md:flex-row">
                <div
                  v-for="(item, idx) in formasi.data"
                  :key="item.id || idx"
                  class="relative flex-1 flex flex-col items-center justify-end min-h-[420px]"
                >
                  <img
                    :src="getFotoUrl(item.dosen?.foto, item.dosen?.nama)"
                    :alt="`Foto ${item.dosen?.nama || 'Pimpinan'}`"
                    class="w-full h-[420px] object-cover object-center"
                  />
                  <div
                    class="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/75 via-black/10 to-transparent"
                  >
                    <div class="p-6 pb-5">
                      <div class="mb-1 text-lg font-bold leading-snug text-center text-white line-clamp-3">
                        {{ item.dosen?.nama || '-' }}
                      </div>
                      <div class="mb-5 text-sm font-medium leading-tight text-center text-white/80">
                        {{ item.jabatan?.namajabatan || '-' }}
                      </div>
                      <button
                        class="w-full py-2 text-white font-medium border border-white rounded-lg bg-transparent hover:bg-white hover:text-primary transition"
                        @click="viewProfile(item.id)"
                      >
                        Lihat Profil
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </main>

      <!-- Modal Profil Pimpinan -->
      <Loading v-if="isLoading" :active="isLoading" :can-cancel="false" :is-full-page="true" />
      <ProfilDosen
        v-if="isModalOpen && !isLoading"
        v-model:isOpen="isModalOpen"
        :dosendata="viewFormasi?.dosen ?? {}"
        :title="viewFormasi?.jabatan?.namajabatan ?? 'Profil Pimpinan'"
      />
    </template>
    <template #footer>
      <FooterFe />
    </template>
  </Container>
</template>

<script setup>
import { ref, computed } from "vue";
import Container from "@/Layouts/Container.vue";
import HeaderFe from "@/Components/HeaderFe.vue";
import PageHero from "@/Components/Web/PageHero.vue";
import FooterFe from "@/Components/FooterFe.vue";
import ProfilDosen from "@/Components/Web/ProfilDosen.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

const props = defineProps({
  profil: { type: Object, required: true },
  formasi: { type: Object, required: true },
});

const p = computed(() => {
  const d = props.profil?.data ?? props.profil;
  return Array.isArray(d) ? d[0] || {} : d || {};
});

const isModalOpen = ref(false);
const viewFormasi = ref(null);
const isLoading = ref(false);

function getFotoUrl(foto, nama = "User") {
  if (!foto) {
    const initials = nama.split(" ").map((n) => n[0]).join("").substring(0, 2);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=e5e7eb&color=6b7280&size=256`;
  }
  if (/^https?:\/\//.test(foto) || foto.startsWith("/storage")) return foto;
  return "/storage/" + foto;
}

const viewProfile = async (id) => {
  isLoading.value = true;
  isModalOpen.value = true;
  viewFormasi.value = null;
  try {
    const response = await fetch(`/pejabat/${id}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const ct = response.headers.get("content-type");
    if (!ct || !ct.includes("application/json")) throw new Error("Response is not JSON");
    const data = await response.json();
    viewFormasi.value = data.formasidata;
  } catch (error) {
    console.error("Error fetching data:", error.message);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
:deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5rem;
  margin: 1rem 0;
}
:deep(ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 1rem 0;
}
:deep(li) {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}
:deep(p) {
  margin: 0.5rem 0;
}
</style>
